import { useCallback, useEffect, useState } from "react";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import { useFleetOptions } from "../hooks/useFleetOptions.js";
import {
  APK_CONTENT_TYPE,
  formatBytes,
  formatWhen,
  type KioskRelease,
} from "../lib/kioskReleases.js";

interface ReleasesResponse {
  storage_configured: boolean;
  storage_location: string | null;
  releases: KioskRelease[];
}

const inputClass =
  "w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary focus:outline-none focus:border-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

/**
 * Upload kiosk APKs and roll them out fleet-wide. Per-table targeting lives on
 * the device page, which is also where a canary goes first: a fleet rollout is
 * the same per-device action applied to every table, each still gated on being
 * idle before it installs.
 */
export function KioskReleases() {
  const { user } = useAuth();
  const { options: fleetOptions } = useFleetOptions();
  const [data, setData] = useState<ReleasesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const [rolloutFor, setRolloutFor] = useState<KioskRelease | null>(null);
  const [rolloutFleetId, setRolloutFleetId] = useState("");
  const [rolloutConfirming, setRolloutConfirming] = useState(false);
  const [rollingOut, setRollingOut] = useState(false);
  const [rolloutMessage, setRolloutMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/admin/kiosk-releases"]["get"]({
        parameters: {},
        body: null,
      });
      if (res.code !== 200) {
        setError("Could not load kiosk releases.");
        return;
      }
      setData(res.body as ReleasesResponse);
      setError(null);
    } catch {
      setError("Could not reach the server to load kiosk releases.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadMessage(null);
    try {
      const base = window.serverConfig?.API_URL || window.location.origin;
      const query = notes.trim() ? `?notes=${encodeURIComponent(notes.trim())}` : "";
      const res = await fetch(`${base}/api/v1/admin/kiosk-releases${query}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": APK_CONTENT_TYPE },
        body: file,
      });
      const body = (await res.json().catch(() => ({}))) as {
        message?: string;
        version_code?: number;
        version_name?: string;
      };
      if (res.status !== 201) {
        setUploadMessage({
          type: "error",
          text: body.message ?? `Upload failed (${res.status}).`,
        });
        return;
      }
      setUploadMessage({
        type: "success",
        text: `Uploaded ${body.version_name} (version code ${body.version_code}).`,
      });
      setFile(null);
      setNotes("");
      await load();
    } catch {
      setUploadMessage({ type: "error", text: "Could not reach the server to upload." });
    } finally {
      setUploading(false);
    }
  };

  const openRollout = (release: KioskRelease) => {
    setRolloutFor(release);
    setRolloutFleetId("");
    setRolloutConfirming(false);
    setRolloutMessage(null);
  };

  const rollout = async () => {
    if (!rolloutFor || !rolloutFleetId) return;
    setRollingOut(true);
    setRolloutMessage(null);
    try {
      const res = await apiClient["/api/v1/admin/kiosk-releases/{id}/rollout"]["post"]({
        parameters: { id: rolloutFor.id },
        body: { fleet_id: rolloutFleetId },
      });
      if (res.code !== 200) {
        setRolloutMessage({ type: "error", text: `Rollout failed (${res.code}).` });
        return;
      }
      const fleetName = fleetOptions.find((f) => f.id === rolloutFleetId)?.name ?? "the fleet";
      setRolloutMessage({
        type: "success",
        text:
          `Queued for ${res.body.targeted} table${res.body.targeted === 1 ? "" : "s"} in ${fleetName}. ` +
          `Skipped ${res.body.skipped_current} already on this version or newer and ` +
          `${res.body.skipped_open} with another update in flight.`,
      });
      setRolloutConfirming(false);
      await load();
    } catch {
      setRolloutMessage({ type: "error", text: "Could not reach the server to roll out." });
    } finally {
      setRollingOut(false);
    }
  };

  if (!user?.is_platform_admin) {
    return (
      <div className="p-6">
        <p className="text-text-secondary">
          Only TeamTable platform admins can manage kiosk app releases.
        </p>
      </div>
    );
  }

  if (loading) {
    return <div className="p-6 text-text-muted">Loading…</div>;
  }

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-xl font-bold text-text-primary mb-1">Kiosk App Releases</h1>
      <p className="text-sm text-text-muted mb-6">
        The Android app that runs on every table. Upload a build here, prove it on one table from
        its device page, then roll it out to a fleet. Compatible tables install at startup or
        scheduled downtime, with no live session. Payments pause during installation and
        verification. Older APKs need an attended bootstrap.
      </p>

      {error && (
        <div className="mb-4 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}

      {data && !data.storage_configured && (
        <div className="mb-4 px-3 py-2 rounded-md bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-sm">
          APK storage is not configured on this environment (neither KIOSK_RELEASES_BUCKET nor
          KIOSK_RELEASES_DIR is set), so uploads and downloads will fail.
        </div>
      )}

      {/* Upload */}
      <section className="mb-6 border border-border rounded-md p-4">
        <h2 className="text-sm font-medium text-text-secondary mb-3">Upload a build</h2>
        <p className="text-xs text-text-muted mb-3">
          Package and versions are read from the APK itself. The UTC timestamp version name must sort
          after existing releases, its Android code must also increase, and the table refuses anything not signed with
          its own certificate.
        </p>
        <div className="grid gap-3 md:grid-cols-[1fr_2fr_auto] items-end">
          <div>
            <label className="block text-xs text-text-secondary mb-1" htmlFor="kiosk-apk">
              APK file
            </label>
            <input
              id="kiosk-apk"
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              aria-label="Kiosk APK"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setUploadMessage(null);
              }}
              disabled={uploading}
              className="w-full text-sm text-text-secondary file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border file:border-border file:bg-surface file:text-text-primary"
            />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1" htmlFor="kiosk-notes">
              Notes
            </label>
            <input
              id="kiosk-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What changed, and where it has been tested"
              aria-label="Release notes"
              disabled={uploading}
              className={inputClass}
            />
          </div>
          <button
            onClick={() => void upload()}
            disabled={!file || uploading || (data ? !data.storage_configured : true)}
            className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? "Uploading…" : "Upload APK"}
          </button>
        </div>
        {uploadMessage && (
          <p
            className={`text-sm mt-3 ${uploadMessage.type === "success" ? "text-green-400" : "text-red-400"}`}
          >
            {uploadMessage.text}
          </p>
        )}
      </section>

      {/* Releases */}
      <section className="border border-border rounded-md p-4">
        <h2 className="text-sm font-medium text-text-secondary mb-3">Releases</h2>
        {!data || data.releases.length === 0 ? (
          <p className="text-sm text-text-muted">No releases uploaded yet.</p>
        ) : (
          <div className="space-y-3">
            {data.releases.map((release, index) => (
              <div
                key={release.id}
                data-testid="kiosk-release"
                className="bg-surface-raised border border-border rounded-md p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-text-primary">{release.version_name}</span>
                      <span className="text-xs text-text-muted font-mono">
                        code {release.version_code}
                      </span>
                      {index === 0 && (
                        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/20 text-blue-400">
                          Newest
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-text-muted mt-1 space-x-3">
                      <span>{formatBytes(release.size_bytes)}</span>
                      <span className="font-mono" title={release.sha256}>
                        sha256 {release.sha256.slice(0, 12)}…
                      </span>
                      <span>
                        {formatWhen(release.created_at)}
                        {release.uploaded_by_email ? ` by ${release.uploaded_by_email}` : ""}
                      </span>
                    </div>
                    {release.notes && (
                      <p className="text-sm text-text-secondary mt-2 whitespace-pre-wrap">
                        {release.notes}
                      </p>
                    )}
                    <div className="text-xs text-text-secondary mt-2">
                      Running on {release.installed_device_count} table
                      {release.installed_device_count === 1 ? "" : "s"}
                      {release.open_update_count > 0 &&
                        `, ${release.open_update_count} update${release.open_update_count === 1 ? "" : "s"} in flight`}
                    </div>
                  </div>
                  <button
                    onClick={() => openRollout(release)}
                    className="shrink-0 px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Roll out to a fleet…
                  </button>
                </div>

                {rolloutFor?.id === release.id && (
                  <div className="mt-4 p-4 rounded-md border border-yellow-500/50 bg-yellow-500/10">
                    <p className="text-sm text-text-primary font-medium mb-2">
                      Roll out {release.version_name} to every table in a fleet
                    </p>
                    <p className="text-xs text-text-secondary mb-3">
                      Each compatible table downloads in the background and installs at startup or
                      scheduled downtime. Older APKs need an attended bootstrap. Tables already on
                      this version or newer, and tables with another update in flight, are skipped.
                      Prove the build on one table first from its device page.
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <select
                        value={rolloutFleetId}
                        onChange={(e) => {
                          setRolloutFleetId(e.target.value);
                          setRolloutConfirming(false);
                        }}
                        aria-label="Rollout fleet"
                        className="px-3 py-2 rounded-md bg-surface border border-border text-text-primary text-sm"
                      >
                        <option value="">Choose a fleet…</option>
                        {fleetOptions.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                      {!rolloutConfirming ? (
                        <button
                          onClick={() => setRolloutConfirming(true)}
                          disabled={!rolloutFleetId || rollingOut}
                          className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Roll out
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => void rollout()}
                            disabled={rollingOut}
                            className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
                          >
                            {rollingOut ? "Queueing…" : "Confirm rollout"}
                          </button>
                          <button
                            onClick={() => setRolloutConfirming(false)}
                            className="px-4 py-2 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors"
                          >
                            Back
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setRolloutFor(null)}
                        className="ml-auto text-sm text-text-secondary hover:text-text-primary transition-colors"
                      >
                        Close
                      </button>
                    </div>
                    {rolloutMessage && (
                      <p
                        className={`text-sm mt-3 ${rolloutMessage.type === "success" ? "text-green-400" : "text-red-400"}`}
                      >
                        {rolloutMessage.text}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
